import type { JSXNode, MarkupOptions } from "shelving";
import { AUTOLINK_RULE, LINK_RULE, LinkRegExpMarkupRule, MARKUP_RULES, NamedRegExpMarkupRule, getBlockRegExp, renderMarkup } from "shelving";
import { useLazy } from "shelving/react";
import { CardLoader } from "shared/react";
import { AT_TAG_RULE, ENTITY_TAG_RULE, HASH_TAG_RULE } from "shared/schema";
import { AT_TAG_REGEXP, ENTITY_TAG_REGEXP, HASH_TAG_REGEXP } from "shared/util/tag";
import { EntityButton } from "../entity/EntityButton.js";
import { EntityCard } from "../entity/EntityCard.js";
import { AtTagButton } from "../tag/AtTagButton.js";
import { AtTagCard } from "../tag/AtTagCard.js";
import { HashTagButton } from "../tag/HashTagButton.js";
import { LinkButton } from "./LinkButton.js";
import { LinkEmbed } from "./LinkEmbed.js";

const EMBED_LINK_RULE = new LinkRegExpMarkupRule(
	getBlockRegExp(LINK_RULE.regexp), //
	(title, href) => <LinkEmbed title={title} href={href} />,
	["block"],
);

const EMBED_AUTOLINK_RULE = new LinkRegExpMarkupRule(
	getBlockRegExp(AUTOLINK_RULE.regexp), //
	(title, href) => <LinkEmbed title={title} href={href} />,
	["block"],
);

const EMBED_HASH_TAG_RULE = new NamedRegExpMarkupRule(
	getBlockRegExp(HASH_TAG_REGEXP), //
	data => <HashTagButton {...data} />,
	["block"],
);

const EMBED_AT_TAG_RULE = new NamedRegExpMarkupRule(
	getBlockRegExp(AT_TAG_REGEXP), //
	data => (
		<CardLoader>
			<AtTagCard {...data} />
		</CardLoader>
	),
	["block"],
);

const EMBED_ENTITY_TAG_RULE = new NamedRegExpMarkupRule(
	getBlockRegExp(ENTITY_TAG_REGEXP), //
	data => (
		<CardLoader>
			<EntityCard {...data} />
		</CardLoader>
	),
	["block"],
);

const BUTTONS_AUTOLINK_RULE = new LinkRegExpMarkupRule(
	AUTOLINK_RULE.regexp, //
	(title, href) => <LinkButton title={title} href={href} />,
	["buttons"],
);

const BUTTONS_LINK_RULE = new LinkRegExpMarkupRule(
	LINK_RULE.regexp, //
	(title, href) => <LinkButton title={title} href={href} />,
	["buttons"],
);

const BUTTONS_HASH_TAG_RULE = new NamedRegExpMarkupRule(
	HASH_TAG_REGEXP, //
	data => <HashTagButton {...data} />,
	["buttons"],
);

const BUTTONS_AT_TAG_RULE = new NamedRegExpMarkupRule(
	AT_TAG_REGEXP, //
	data => <AtTagButton {...data} />,
	["buttons"],
);

const BUTTONS_ENTITY_TAG_RULE = new NamedRegExpMarkupRule(
	ENTITY_TAG_REGEXP, //
	data => <EntityButton {...data} />,
	["buttons"],
);

/**
 * Buttons row
 * - A block/paragraph comprising only buttons of any type (link/autolink or hash/at/entity tag).
 */
const BUTTONS_ANY = `${AUTOLINK_RULE.regexp.source}|${LINK_RULE.regexp.source}|${HASH_TAG_REGEXP.source}|${AT_TAG_REGEXP.source}|${ENTITY_TAG_REGEXP.source}`.replace(/\?<[a-z]+>/g, "?:"); // Remove the `?<named>` placeholders from the source regexps otherwise the names clash.
const BUTTONS_RULE = new NamedRegExpMarkupRule(
	getBlockRegExp(`(?<buttons>(?:${BUTTONS_ANY})(?:\\s*${BUTTONS_ANY})*)`), //
	({ buttons }) => ({
		...(<p className="flex-row flex-center flex-wrap gap-xsmall size-small">{buttons.trim()}</p>),
		context: "buttons", // Child context parses the buttons.
	}),
	["block"],
);

/** Render markup with embeds. */
export function renderMarkupWithEmbeds(content: string): JSXNode {
	return renderMarkup(content, _EMBED_MARKUP_OPTIONS);
}
const _EMBED_MARKUP_OPTIONS: Partial<MarkupOptions> = {
	rules: [
		...MARKUP_RULES,
		EMBED_LINK_RULE,
		EMBED_AUTOLINK_RULE,
		EMBED_HASH_TAG_RULE,
		EMBED_AT_TAG_RULE,
		EMBED_ENTITY_TAG_RULE,
		HASH_TAG_RULE,
		AT_TAG_RULE,
		ENTITY_TAG_RULE,
		BUTTONS_LINK_RULE,
		BUTTONS_AUTOLINK_RULE,
		BUTTONS_HASH_TAG_RULE,
		BUTTONS_AT_TAG_RULE,
		BUTTONS_ENTITY_TAG_RULE,
		BUTTONS_RULE,
	],
	rel: "nofollow ugc",
};

/** Markup that shows various embeds based on `@tags` and `#tags` and `type:id` tags. */
export function EmbedMarkup({ children }: { children: string }): React.ReactElement {
	return <>{useLazy(renderMarkupWithEmbeds, children)}</>;
}
